import LegalLayout from "./LegalLayout";
import { LEGAL, listPlaceholders, isPlaceholder } from "../../lib/legal-config";

/**
 * Impressum nach § 5 DDG (früher TMG) und § 18 Abs. 2 MStV.
 *
 * Pflichtangaben:
 *   1. Name und Anschrift (postalisch, keine Postfächer)
 *   2. Bei juristischen Personen: Rechtsform + Vertretungsberechtigte
 *   3. Kontakt: E-Mail + schnelle elektronische Kommunikation
 *   4. Handelsregister-Angaben (HRB/Registergericht) soweit einschlägig
 *   5. USt-ID nach § 27a UStG soweit einschlägig
 *   6. Für redaktionelle Inhalte: inhaltlich Verantwortlicher nach § 18 Abs. 2 MStV
 *
 * Die Komponente lädt die Daten aus `legal-config.ts`. Solange dort noch
 * PLACEHOLDER_-Werte stehen, wird ein auffälliger Warnhinweis oben
 * eingeblendet — damit niemand versehentlich mit Dummy-Impressum online geht.
 */
export default function Impressum() {
  const placeholders = listPlaceholders();
  const hasMissing = placeholders.length > 0;

  return (
    <LegalLayout title="Impressum" lastUpdated="2026-04-22">
      {hasMissing && (
        <div className="rounded-lg border border-red-700 bg-red-950/50 p-4 text-sm text-red-200">
          <p className="font-semibold text-red-100">⚠ Entwurf — noch nicht vollständig</p>
          <p className="mt-1">
            Dieses Impressum enthält noch Platzhalter und erfüllt <strong>nicht</strong> die
            Pflichtangaben nach § 5 DDG. Vor dem Livegang müssen folgende Felder in{" "}
            <code className="font-mono bg-slate-900 px-1 rounded">src/lib/legal-config.ts</code>{" "}
            ersetzt werden:
          </p>
          <ul className="mt-2 list-disc pl-5 font-mono text-xs">
            {placeholders.map((p) => <li key={p}>{p}</li>)}
          </ul>
        </div>
      )}

      <h2>Angaben gemäß § 5 DDG</h2>
      <p>
        <strong>{renderValue(LEGAL.companyName)}</strong><br />
        {renderValue(LEGAL.streetAddress)}<br />
        {renderValue(LEGAL.postalCodeCity)}<br />
        {LEGAL.country}
      </p>

      <h3>Vertreten durch</h3>
      <p>{renderValue(LEGAL.managingDirectors)}</p>

      <h3>Kontakt</h3>
      <p>
        E-Mail: <a href={`mailto:${LEGAL.contactEmail}`}>{LEGAL.contactEmail}</a>
        {LEGAL.phone ? (<><br />Telefon: {LEGAL.phone}</>) : null}
      </p>

      {(LEGAL.registryCourt || LEGAL.registryNumber) && (
        <>
          <h3>Handelsregister</h3>
          <p>
            {LEGAL.registryCourt ?? "—"}
            {LEGAL.registryNumber ? <>, {LEGAL.registryNumber}</> : null}
          </p>
        </>
      )}

      {LEGAL.vatId && (
        <>
          <h3>Umsatzsteuer-Identifikationsnummer</h3>
          <p>Gemäß § 27a UStG: {LEGAL.vatId}</p>
        </>
      )}

      <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
      <p>
        {renderValue(LEGAL.journalisticResponsible ?? LEGAL.responsiblePerson)}<br />
        (Anschrift wie oben)
      </p>

      <h2>EU-Streitschlichtung</h2>
      <p>
        Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{" "}
        <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">
          https://ec.europa.eu/consumers/odr
        </a>
        . Unsere E-Mail-Adresse finden Sie oben im Impressum.
      </p>

      <h2>Verbraucherstreitbeilegung / Universalschlichtungsstelle</h2>
      <p>
        Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer
        Verbraucherschlichtungsstelle teilzunehmen.
      </p>

      <h2>Haftung für Inhalte</h2>
      <p>
        Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten nach
        den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter
        jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu
        überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen.
        Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den
        allgemeinen Gesetzen bleiben hiervon unberührt.
      </p>

      <h2>Urheberrecht</h2>
      <p>
        Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen
        dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der
        Verwertung außerhalb der Grenzen des Urheberrechtes bedürfen der schriftlichen Zustimmung
        des jeweiligen Autors bzw. Erstellers.
      </p>

      <h2>Hinweis zu Mock-Jury-Simulationen (keine Rechtsberatung)</h2>
      <p>
        Shadow Court ist ein <strong>technisches Prognose- und Simulationswerkzeug</strong>. Die
        vom System berechneten Gewinnwahrscheinlichkeiten, Schadensersatz-Schätzungen und
        Vergleichsempfehlungen stellen keine Rechtsberatung im Sinne des{" "}
        Rechtsdienstleistungsgesetzes (RDG) dar. Für individuelle Rechtsberatung wenden Sie sich
        bitte an einen zugelassenen Rechtsanwalt. Wir übernehmen keine Gewähr für die inhaltliche
        Richtigkeit, Vollständigkeit oder Aktualität der Ergebnisse.
      </p>
    </LegalLayout>
  );
}

/** Rendert einen Platzhalter rot, damit im DOM sofort sichtbar ist, was fehlt. */
function renderValue(v: string) {
  if (isPlaceholder(v)) {
    return (
      <span className="text-red-300 bg-red-950/40 px-2 py-0.5 rounded font-mono text-xs">
        [{v}]
      </span>
    );
  }
  return <>{v}</>;
}
